import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { validateCPF, validateEmail, validatePhone } from '../../utils/validators';
import { formatCPF, formatPhone, cleanCPF, cleanPhone } from '../../utils/formatters';
import { USER_ROLES, USER_STATUS } from '../../utils/constants';
import Button from '../common/Button';
import PhotoUpload from './PhotoUpload';

const UserForm = ({ initialData, onSubmit, isLoading }) => {
  const isEdit = !!initialData;
  const [photoFile, setPhotoFile] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    defaultValues: initialData || {
      name: '',
      email: '',
      cpf: '',
      phone: '',
      birthDate: '',
      status: 'active',
      role: 'user',
    },
  });

  const cpfValue = watch('cpf');
  const phoneValue = watch('phone');

  // Mantém o campo oculto de validação sincronizado com o arquivo selecionado
  useEffect(() => {
    setValue('photo', photoFile, { shouldValidate: true });
  }, [photoFile, setValue]);

  // Format CPF on change
  useEffect(() => {
    if (cpfValue) {
      const formatted = formatCPF(cpfValue);
      if (formatted !== cpfValue) {
        setValue('cpf', formatted);
      }
    }
  }, [cpfValue, setValue]);

  // Format phone on change
  useEffect(() => {
    if (phoneValue) {
      const formatted = formatPhone(phoneValue);
      if (formatted !== phoneValue) {
        setValue('phone', formatted);
      }
    }
  }, [phoneValue, setValue]);

  const handleFormSubmit = (data) => {
    // Clean CPF and phone before submitting; anexa o arquivo de foto
    const { photo, ...rest } = data;
    const cleanedData = {
      ...rest,
      cpf: cleanCPF(data.cpf),
      phone: cleanPhone(data.phone),
      photoFile: photoFile || null,
    };
    onSubmit(cleanedData);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Name */}
        <div className="md:col-span-2">
          <label htmlFor="name" className="label">
            Nome Completo *
          </label>
          <input
            id="name"
            type="text"
            className={`input ${errors.name ? 'input-error' : ''}`}
            {...register('name', {
              required: 'Nome é obrigatório',
              minLength: {
                value: 3,
                message: 'Nome deve ter no mínimo 3 caracteres',
              },
            })}
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
          )}
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className="label">
            Email *
          </label>
          <input
            id="email"
            type="email"
            className={`input ${errors.email ? 'input-error' : ''}`}
            {...register('email', {
              required: 'Email é obrigatório',
              validate: (value) => validateEmail(value) || 'Email inválido',
            })}
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
          )}
        </div>

        {/* CPF */}
        <div>
          <label htmlFor="cpf" className="label">
            CPF *
          </label>
          <input
            id="cpf"
            type="text"
            maxLength="14"
            className={`input ${errors.cpf ? 'input-error' : ''}`}
            {...register('cpf', {
              required: 'CPF é obrigatório',
              validate: (value) => validateCPF(value) || 'CPF inválido',
            })}
          />
          {errors.cpf && (
            <p className="mt-1 text-sm text-red-600">{errors.cpf.message}</p>
          )}
        </div>

        {/* Phone */}
        <div>
          <label htmlFor="phone" className="label">
            Telefone
          </label>
          <input
            id="phone"
            type="text"
            maxLength="15"
            className={`input ${errors.phone ? 'input-error' : ''}`}
            {...register('phone', {
              validate: (value) =>
                !value || validatePhone(value) || 'Telefone inválido',
            })}
          />
          {errors.phone && (
            <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
          )}
        </div>

        {/* Birth Date */}
        <div>
          <label htmlFor="birthDate" className="label">
            Data de Nascimento
          </label>
          <input
            id="birthDate"
            type="date"
            className={`input ${errors.birthDate ? 'input-error' : ''}`}
            {...register('birthDate')}
          />
          {errors.birthDate && (
            <p className="mt-1 text-sm text-red-600">{errors.birthDate.message}</p>
          )}
        </div>

        {/* Status */}
        <div>
          <label htmlFor="status" className="label">
            Status *
          </label>
          <select
            id="status"
            className={`input ${errors.status ? 'input-error' : ''}`}
            {...register('status', { required: 'Status é obrigatório' })}
          >
            <option value={USER_STATUS.ACTIVE}>Ativo</option>
            <option value={USER_STATUS.INACTIVE}>Inativo</option>
            <option value={USER_STATUS.PENDING}>Pendente</option>
          </select>
          {errors.status && (
            <p className="mt-1 text-sm text-red-600">{errors.status.message}</p>
          )}
        </div>

        {/* Role */}
        <div>
          <label htmlFor="role" className="label">
            Função *
          </label>
          <select
            id="role"
            className={`input ${errors.role ? 'input-error' : ''}`}
            {...register('role', { required: 'Função é obrigatória' })}
          >
            <option value={USER_ROLES.USER}>Usuário</option>
            <option value={USER_ROLES.ADMIN}>Administrador</option>
          </select>
          {errors.role && (
            <p className="mt-1 text-sm text-red-600">{errors.role.message}</p>
          )}
        </div>

        {/* Foto */}
        <div className="md:col-span-2">
          <PhotoUpload
            label={`Foto${isEdit ? '' : ' *'}`}
            value={photoFile}
            onChange={setPhotoFile}
            existingUrl={initialData?.photoUrl || null}
            error={errors.photo?.message}
          />
          {/* Campo oculto para forçar validação de foto obrigatória no cadastro */}
          <input
            type="hidden"
            {...register('photo', {
              validate: (value) =>
                isEdit || value ? true : 'A foto é obrigatória.',
            })}
          />
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
        <Button
          type="button"
          variant="secondary"
          onClick={() => window.history.back()}
        >
          Cancelar
        </Button>
        <Button type="submit" variant="primary" loading={isLoading}>
          {initialData ? 'Atualizar' : 'Cadastrar'}
        </Button>
      </div>
    </form>
  );
};

export default UserForm;

